import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, Clock, Inbox, RotateCcw, XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthGuard from '../components/auth/AuthGuard';
import RefreshButton from '../components/common/RefreshButton';
import { outboxApi } from '../services/api';

/**
 * Outbox operability (EP-18 FE-3).
 *
 * "A dead letter with no visible reason is indistinguishable from work that
 * silently vanished. This is what you need at 2am, and it is the part that
 * gets cut."
 *
 * So this page leads with the two numbers that decide whether delivery is
 * healthy — depth and dead letters — and shows the ERROR on the row rather
 * than behind a click. A queue is silent by nature: nothing fails visibly,
 * entries just stop moving.
 */

const STATUS_STYLE: Record<string, string> = {
  pending: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  claimed: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  delivered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  failed: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  dead: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  discarded: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};

const FILTERS = ['all', 'dead', 'failed', 'pending', 'delivered', 'discarded'] as const;

export default function AdminOutboxPage() {
  const [health, setHealth] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('all');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const [h, s, list] = await Promise.all([
        outboxApi.health(),
        outboxApi.stats(),
        outboxApi.list(filter === 'all' ? undefined : filter),
      ]);
      setHealth(h);
      setStats(s);
      setRows(list.items || []);
    } catch (e: any) {
      toast.error(e.message || 'Failed to load the outbox');
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const retry = async (id: string) => {
    setBusy(id);
    try {
      await outboxApi.retry(id);
      toast.success('Requeued');
      await load();
    } catch (e: any) {
      toast.error(e.message || 'Retry failed');
    } finally { setBusy(''); }
  };

  const discard = async (id: string) => {
    const reason = prompt('Why is this being abandoned? (kept on the record)');
    if (reason === null) return;
    setBusy(id);
    try {
      await outboxApi.discard(id, reason);
      toast.success('Discarded');
      await load();
    } catch (e: any) {
      toast.error(e.message || 'Discard failed');
    } finally { setBusy(''); }
  };

  const banner = health?.status === 'stalled'
    ? { cls: 'border-red-300 dark:border-red-700/60 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300', Icon: XCircle, label: 'Outbox is not draining' }
    : health?.status === 'degraded'
      ? { cls: 'border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300', Icon: AlertTriangle, label: 'Delivery is working; something needs a decision' }
      : { cls: 'border-emerald-300 dark:border-emerald-700/60 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300', Icon: CheckCircle2, label: 'Draining normally' };

  return (
    <AuthGuard roles={['project_admin', 'super_admin']}>
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-500/10 text-sky-600 flex items-center justify-center">
              <Inbox size={20} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Outbox</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Durable delivery of effects to systems outside PMO
              </p>
            </div>
          </div>
          <RefreshButton onRefresh={load} tooltip="Refresh" />
        </div>

        {health && (
          <div className={`mb-4 rounded-lg border p-3 ${banner.cls}`}>
            <div className="flex items-center gap-2 text-sm font-medium">
              <banner.Icon size={16} /> {banner.label}
            </div>
            {health.reasons?.length > 0 && (
              <ul className="mt-1.5 ml-6 list-disc text-xs opacity-90">
                {health.reasons.map((r: string, i: number) => <li key={i}>{r}</li>)}
              </ul>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <Stat label="Queue depth" value={stats?.depth ?? '—'}
                hint="waiting or being delivered" />
          <Stat label="Dead letters" value={stats?.dead ?? '—'} danger={!!stats?.dead}
                hint="need a decision" />
          <Stat label="Oldest waiting"
                value={health ? formatAge(health.oldestPendingAgeSeconds) : '—'}
                hint="how far behind delivery is" />
          <Stat label="Stuck claims" value={health?.stuckClaims ?? '—'}
                danger={!!health?.stuckClaims}
                hint="a dispatcher died mid-delivery" />
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`text-xs px-2.5 py-1.5 rounded-md ${
                filter === f ? 'bg-accent text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
              {f}
              {stats?.counts?.[f] != null && (
                <span className="ml-1.5 opacity-70">{stats.counts[f]}</span>
              )}
            </button>
          ))}
        </div>

        <div className="grid gap-2">
          {rows.map((e) => (
            <div key={e._id}
                 className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
              <div className="flex items-start gap-3">
                <span className={`text-[11px] px-2 py-0.5 rounded shrink-0 ${
                  STATUS_STYLE[e.status] || STATUS_STYLE.discarded}`}>
                  {e.status}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {e.effectType}
                    {e.entityType && (
                      <span className="ml-2 text-xs font-normal text-gray-400">
                        {e.entityType} · {e.transition}
                      </span>
                    )}
                  </div>
                  {/* The error goes ON the row. A dead letter with no visible
                      reason is indistinguishable from work that vanished. */}
                  {e.lastError && (
                    <div className="mt-1 text-xs text-red-600 dark:text-red-400 break-words">
                      {e.lastError}
                    </div>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock size={11} /> {new Date(e.createdAt).toLocaleString()}
                    </span>
                    <span>attempt {e.attempts ?? 0}</span>
                    {e.deadReason && <span>dead: {e.deadReason}</span>}
                    {e.discardReason && <span>discarded: {e.discardReason}</span>}
                    <span className="font-mono opacity-60">
                      key {String(e.idempotencyKey || '').slice(0, 12)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {e.status !== 'delivered' && (
                    <>
                      {/* Replay is safe BECAUSE the key is derived: replaying
                          something that actually succeeded returns the original
                          execution rather than doing it twice. */}
                      <button onClick={() => retry(e._id)} disabled={busy === e._id}
                        className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-accent text-white disabled:opacity-40">
                        <RotateCcw size={12} /> Retry
                      </button>
                      {e.status !== 'discarded' && (
                        <button onClick={() => discard(e._id)} disabled={busy === e._id}
                          className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-40">
                          Discard
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <div className="text-sm text-gray-400 py-10 text-center">
              Nothing {filter === 'all' ? 'in the outbox' : `with status "${filter}"`}.
            </div>
          )}
        </div>

        <p className="mt-6 text-xs text-gray-400">
          Retrying resets the attempt count — a dead letter is usually retried
          because the cause was fixed. Discarding keeps the record and the
          reason; it is not a delete.
        </p>
      </div>
    </AuthGuard>
  );
}

function Stat({ label, value, hint, danger }: any) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-2xl font-semibold ${
        danger ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
        {value}
      </div>
      <div className="text-[11px] text-gray-400">{hint}</div>
    </div>
  );
}

function formatAge(seconds: number) {
  if (!seconds) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}
